import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/components/atoms/Button';
import CloseButton from '@/components/atoms/CloseButton';
import SubtitleText from '@/components/atoms/SubtitleText';
import TextComponent from '@/components/atoms/TextComponent';
import { SystemParameter } from '@/constants/SystemParametersConstants';
import api from '@/services/apiSgft';

import SimpleTextField from '../atoms/SimpleTextField';
import ModalConfirmation from './ConfirmationModal';

interface SystemParameterModalProps {
	parameter: SystemParameter;
	modalIsOpen: boolean;
	handleClose: () => void;
	confirmationText: string;
}

export const SystemParameterModal = ({
	parameter,
	modalIsOpen,
	handleClose,
	confirmationText,
}: SystemParameterModalProps) => {
	const queryClient = useQueryClient();
	const [value, setValue] = useState(parameter.value);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

	const handleSaveButton = () => {
		if (!value || value.trim() === '') {
			toast.error('É necessário inserir um valor', {
				theme: 'colored',
			});
			return;
		}

		if (value === parameter.value) {
			toast.error('Valor não alterado', {
				theme: 'colored',
			});
			return;
		}

		if (isNaN(Number(value))) {
			toast.error('Valor deve ser numérico', {
				theme: 'colored',
			});
			return;
		}

		if (Number(value) < 0) {
			toast.error('Valor não pode ser negativo', {
				theme: 'colored',
			});
			return;
		}

		setIsConfirmationOpen(true);
	};

	const handleUpdate = async () => {
		setIsSubmitting(true);

		try {
			await api.patch(`/system-parameters/${parameter.id}`, {
				value,
			});

			toast.success('Parâmetro atualizado com sucesso!', {
				theme: 'colored',
			});

			queryClient.invalidateQueries(['system-parameters']);
			handleCloseConfirmation();
			handleClose();
		} catch (error: any) {
			if (
				error instanceof AxiosError &&
				error?.response?.data?.error === 'PENDING_LOG_ALREADY_EXISTS'
			) {
				toast.warning(
					'O parâmetro foi atualizado, porém a rotina já está em execução. Aguarde um momento e atualize os bloqueios pela tela de bloqueios',
					{
						theme: 'colored',
						toastId: 'warning',
					},
				);
			} else {
				toast.error('Erro ao atualizar o parâmetro', {
					theme: 'colored',
					toastId: 'error',
				});
			}
			throw error;
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCloseConfirmation = () => {
		setIsConfirmationOpen(false);
	};

	return (
		<Transition appear show={modalIsOpen}>
			<Dialog
				className="relative z-50"
				onClose={handleClose}
				open={modalIsOpen}
			>
				<div className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4">
					<Transition.Child
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="custom-scrollbar max-h-[100vh] w-[40rem] flex-col justify-between overflow-auto overflow-y-scroll rounded-lg bg-white">
							<div className="w-full flex-col justify-between p-5">
								<div className="flex h-auto items-center justify-between">
									<div className="flex justify-between">
										<SubtitleText
											className="text-[20px]"
											subtitle="Cadastro de Parâmetro da Catraca"
										/>
									</div>
									<CloseButton onClick={handleClose} className="" />
								</div>

								<div className="mt-5">
									<div className="mb-5 grid w-full grid-cols-1 gap-5">
										<TextComponent
											title="Parâmetro"
											value={parameter.description}
										/>
									</div>

									<div className="flex flex-col">
										<div className="mb-5">
											<SimpleTextField
												fontColor="text-[#4a4a4a]"
												fontSize="text-[12px]"
												title="Período (dias úteis)"
												value={value}
												setState={setValue}
											/>
										</div>

										<div className="flex justify-between pt-4">
											<Button
												type="button"
												title="Cancelar"
												variant="link"
												className="min-w-40 font-semibold ring-1"
												onClick={handleClose}
											>
												CANCELAR
											</Button>
											<Button
												type="button"
												className="min-w-40 "
												onClick={handleSaveButton}
											>
												SALVAR
											</Button>
										</div>
									</div>
									{isConfirmationOpen && (
										<ModalConfirmation
											title="Confirmação de Alteração"
											description={confirmationText}
											confirmAction={handleUpdate}
											isOpen={isConfirmationOpen}
											onClose={handleCloseConfirmation}
											discartAction={handleCloseConfirmation}
											descriptionClassName="text-start text-sm font-normal"
											isLoading={isSubmitting}
										/>
									)}
								</div>
							</div>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
};

export default SystemParameterModal;
